import { formatDate } from '@angular/common';
import {
  Component,
  inject,
  input,
  LOCALE_ID,
  OnChanges,
  signal,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { catchError, map, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { InsuranceFlowService } from 'src/app/_services/insurance-flow.service';
import {
  getInsuranceFlowOptionDetail,
  getInsuranceFlowOptionLabel,
  getInsuranceFlowOptionTitle,
  InsuranceFlowOption,
  InsuranceFlowSearchPurpose,
} from 'src/app/_types/insurance-flow.types';
import {
  getInsuranceFlowLabel,
  getInsuranceFlowStatusClass,
} from 'src/app/enums/insurance-flow/insurance-flow-status.enum';
import {
  getInsuranceTypeLabel,
  InsuranceType,
} from 'src/app/enums/insurance-type.enum';
import { ComboboxComponent } from 'src/app/shared/components/combobox/combobox.component';
import { environment } from 'src/environments/environment';

/**
 * Sentinel first entry of an optional picker: "Aucune affaire".
 *
 * app-combobox only writes the control when an option is picked and clears
 * itself only when the selected option is re-picked by identity — which never
 * happens with server-side results. An explicit entry is the one reliable way
 * to say "none"; picking it lands `null` in the control (see the constructor).
 */
export const NO_FLOW_OPTION = {
  id: '',
  displayId: null,
  status: null,
  policyId: null,
  categoryId: null,
  categoryName: null,
  proposalId: null,
  insuranceType: null,
  createdOn: '',
} as unknown as InsuranceFlowOption;

/**
 * The affaire type-ahead of the cancellation forms: the member's affaires,
 * searched server-side.
 *
 * Owns the option list and its loading; the host hands over the member whose
 * affaires to search, the control that receives the pick, and whether a pick
 * is mandatory. The control holds the whole option rather than an id, so a
 * host can read more than `id` from it.
 * Content projected into the field renders under the input, for a hint.
 */
@Component({
  selector: 'app-flow-select',
  standalone: true,
  imports: [ComboboxComponent],
  template: `
    <div class="form-group mb-16">
      <label>{{ label() }}</label>
      <app-combobox
        [control]="control()"
        [items]="options"
        [loading]="loading"
        [loadingText]="loadingText"
        [(searchValue)]="searchValue"
        [displayValue]="optionLabel"
        [itemTemplate]="optionTpl"
        [placeholder]="placeholder"
        [ariaLabel]="label()"
        [emptyText]="emptyText"
        [errorText]="failed ? errorText : ''"
        [compareWith]="sameFlow"
        (searchChange)="search($event)"
      />
      <ng-content />
    </div>
    <ng-template #optionTpl let-flow>
      <span class="flow-option">
        <span class="flow-option__head">
          <span class="flow-option__id">{{ optionTitle(flow) }}</span>
          @if (flow.status) {
            <span
              class="lty-badge flow-option__status"
              [class]="statusClass(flow.status)"
            >
              {{ statusLabel(flow.status) }}
            </span>
          }
        </span>
        @if (optionDetail(flow)) {
          <span class="flow-option__detail">{{ optionDetail(flow) }}</span>
        }
      </span>
    </ng-template>
  `,
  styleUrls: ['./flow-option.css'],
  // The option styles must reach the overlay panel app-combobox opens.
  encapsulation: ViewEncapsulation.None,
  host: { class: 'flow-select' },
})
export class FlowSelectComponent implements OnChanges {
  private readonly insuranceFlowService = inject(InsuranceFlowService);

  private readonly locale = inject(LOCALE_ID);

  readonly control = input.required<FormControl<InsuranceFlowOption | null>>();

  /**
   * Whose affaires to offer. The search endpoint answers for one member only;
   * empty until the host resolves it, and the picker stays empty meanwhile.
   */
  readonly familyMemberId = input('');

  /** Offer only affaires that carry a proposal (`proposalId` set). */
  readonly withPolicyOnly = input(false);

  /** Offer only affaires of one branch: a cancellation's pickers stay in the cancelled policy's. */
  readonly insuranceType = input<InsuranceType | null>(null);

  /**
   * What the pick is for, so the server offers only the statuses that role
   * accepts. Unset, every status is offered.
   */
  readonly purpose = input<InsuranceFlowSearchPurpose | null>(null);

  /** Drops the "Aucune affaire" entry: the host wants a real pick. */
  readonly required = input(false);

  /** The field's label, and the input's accessible name with it. */
  readonly label = input($localize`Affaire concernée`);

  /**
   * Keeps the picker inside one affaire, matched exactly on its public id.
   * A cancellation's replacement is created as a child of the cancelled
   * policy's flow and so shares its display id — the server rejects any other.
   */
  readonly displayId = input('');

  /**
   * Drops one affaire from the options. A cancellation's replacement picker
   * offers the cancelled policy's own affaire otherwise: it matches the member
   * and the display id it is narrowed to, yet the server rejects it.
   */
  readonly excludeFlowId = input('');

  /**
   * Dates each option with the day its affaire was opened. A replacement picker
   * offers affaires that can share an insurer and even a display id, and the
   * newest is the one taking over — nothing else on the row separates them.
   */
  readonly withCreatedOn = input(false);

  protected options: InsuranceFlowOption[] = [];

  /** What the last search returned, before the sentinel and the exclusion. */
  private found: InsuranceFlowOption[] = [];

  protected loading = false;

  /** The last search failed: the panel says so rather than "nothing found". */
  protected failed = false;

  /** The search the picker opens with, typed text aside, found nothing to offer. */
  private nothingUnderId = false;

  /**
   * The text in the input. app-combobox owns it while the agent types but
   * never derives it from the control, so it is set here whenever the control
   * changes hands — a seeded affaire, a reset, a sentinel pick.
   */
  protected readonly searchValue = signal('');

  /** Queries funnel through one stream so a slow earlier search never overwrites a later one. */
  private readonly query$ = new Subject<string | undefined>();

  constructor() {
    this.query$
      .pipe(
        tap(() => (this.loading = true)),
        switchMap((query) =>
          this.insuranceFlowService
            .searchInsuranceFlows({
              familyMemberId: this.familyMemberId(),
              query: query?.trim() || undefined,
              displayId: this.displayId() || undefined,
              withPolicyOnly: this.withPolicyOnly() || undefined,
              insuranceType: this.insuranceType() || undefined,
              purpose: this.purpose() || undefined,
            })
            .pipe(
              map((res) => ({
                query,
                items: res.data?.items ?? [],
                failed: false,
              })),
              catchError(() =>
                of({ query, items: [] as InsuranceFlowOption[], failed: true })
              )
            )
        ),
        takeUntilDestroyed()
      )
      .subscribe(({ query, items, failed }) => {
        this.failed = failed;
        this.found = items;
        this.options = this.shapeOptions();
        // A failed search says nothing about what sits under the id.
        if (!failed && !query?.trim()) {
          this.nothingUnderId = !this.options.length;
        }
        this.loading = false;
      });

    // Follows the control it is given, and every value that lands in it.
    toObservable(this.control)
      .pipe(
        switchMap((control) =>
          control.valueChanges.pipe(startWith(control.value))
        ),
        takeUntilDestroyed()
      )
      .subscribe((option) => this.onControlValue(option));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.familyMemberId()) return;
    if (
      changes['familyMemberId'] ||
      changes['withPolicyOnly'] ||
      changes['insuranceType'] ||
      changes['purpose'] ||
      changes['displayId']
    ) {
      this.query$.next(undefined);
    } else if (changes['required'] || changes['excludeFlowId']) {
      // Re-shape what the last search returned rather than repeat it: neither
      // the sentinel nor the exclusion changes which affaires match.
      this.options = this.shapeOptions();
    }
  }

  /** The last search's affaires, less the excluded one, plus the sentinel. */
  private shapeOptions(): InsuranceFlowOption[] {
    const excluded = this.excludeFlowId();
    const items = excluded
      ? this.found.filter((option) => option.id !== excluded)
      : this.found;
    return this.required() ? items : [NO_FLOW_OPTION, ...items];
  }

  /** Debounced by app-combobox, so this is already the settled query. */
  protected search(query: string): void {
    if (!this.familyMemberId()) return;
    // The text was just set from the control, not typed: nothing to search.
    const picked = this.control().value;
    if (picked && query === this.optionLabel(picked)) return;
    this.query$.next(query);
  }

  /** The sentinel means "none"; anything else is echoed into the input. */
  private onControlValue(option: InsuranceFlowOption | null): void {
    if (option === NO_FLOW_OPTION) {
      this.control().setValue(null);
      return;
    }
    this.searchValue.set(option ? this.optionLabel(option) : '');
  }

  /**
   * Names the id the search is held to when nothing at all sits under it: the
   * affaire the agent has in mind may be one of the member's, of the same
   * branch, yet under another id. Text that merely matches nothing is just that.
   */
  protected get emptyText(): string {
    const displayId = this.displayId();
    return displayId && this.nothingUnderId
      ? $localize`Aucune affaire avec l'ID ${displayId} trouvée`
      : $localize`Aucune affaire trouvée`;
  }

  protected readonly loadingText = $localize`Chargement des affaires…`;

  protected readonly errorText = $localize`Impossible de charger les affaires`;

  /** Refetched options are new objects: the checkmark pairs them by id. */
  protected readonly sameFlow = (
    option: InsuranceFlowOption,
    value: InsuranceFlowOption
  ): boolean => option.id === value.id;

  protected get placeholder(): string {
    return this.required()
      ? $localize`Choisir une affaire`
      : $localize`Aucune affaire`;
  }

  protected readonly statusLabel = getInsuranceFlowLabel;

  protected readonly statusClass = getInsuranceFlowStatusClass;

  /** The text left in the input once an affaire is picked. */
  protected readonly optionLabel = (option: InsuranceFlowOption): string =>
    option === NO_FLOW_OPTION
      ? $localize`Aucune affaire`
      : getInsuranceFlowOptionLabel(option);

  /** First line of a dropdown row. */
  protected readonly optionTitle = (option: InsuranceFlowOption): string =>
    option === NO_FLOW_OPTION
      ? $localize`Aucune affaire`
      : getInsuranceFlowOptionTitle(option);

  /**
   * Second line of a dropdown row: the insurance type, the insurer, product and
   * policy, then the opening day when the host asks. Blank for the sentinel.
   */
  protected readonly optionDetail = (option: InsuranceFlowOption): string => {
    if (option === NO_FLOW_OPTION) return '';
    return [
      option.insuranceType && getInsuranceTypeLabel(option.insuranceType),
      getInsuranceFlowOptionDetail(option),
      this.withCreatedOn() && option.createdOn
        ? formatDate(option.createdOn, environment.DATE_FORMAT, this.locale)
        : '',
    ]
      .filter(Boolean)
      .join(' · ');
  };
}
